// =============================================================================
// RankingView.ts — Tela de ranking (issue #88), no estilo da mesa de salas:
// tabela com as melhores pontuações, abas de modo (campanha x infinito) e
// filtro por candidato (geral + cada personagem). Os dados vêm do
// GET /api/ranking (Postgres); servidor sem banco mostra o aviso de dev.
// =============================================================================
import { CHARACTER_LIST, CHARACTER_TABLE } from '../../../shared/characters';
import { isRankingMode, type RankingMode, type RankingResponse, type RankingRow } from '../../../shared/ranking';
import { audio } from '../AudioEngine';
import { $, charImg, showScreen, splitNome } from './dom';

// Nome de urna entra em innerHTML: escapa antes que alguém se eleja com <script>
const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

const fmtTempo = (secs: number): string => {
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  return `${mm}m ${String(ss).padStart(2, '0')}s`;
};

export class RankingView {
  private mode: RankingMode = 'campanha';
  private charId: string | null = null;   // null = ranking geral
  private seq = 0;                        // descarta respostas fora de ordem

  constructor(onBack: () => void) {
    $('btn-ranking-back').onclick = () => {
      audio.sfx.urnaKey();
      onBack();
    };

    for (const btn of document.querySelectorAll<HTMLButtonElement>('#rank-modes .rank-tab')) {
      btn.onclick = () => {
        this.mode = isRankingMode(btn.dataset.mode) ? btn.dataset.mode : 'campanha';
        audio.sfx.urnaKey();
        this.syncFiltros();
        void this.load();
      };
    }
    this.buildCharPills();
  }

  show(): void {
    showScreen('ranking');
    this.syncFiltros();
    void this.load();
  }

  // GERAL + um pill por candidato (só o primeiro nome: "Lula", "Datena"...)
  private buildCharPills(): void {
    const box = $('rank-chars');
    box.innerHTML = '';
    const pill = (label: string, charId: string | null): void => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rank-pill';
      btn.dataset.char = charId ?? '';
      btn.textContent = label;
      btn.onclick = () => {
        this.charId = charId;
        audio.sfx.urnaKey();
        this.syncFiltros();
        void this.load();
      };
      box.appendChild(btn);
    };
    pill('GERAL', null);
    for (const c of CHARACTER_LIST) pill(splitNome(c.nome).nome.toUpperCase(), c.id);
  }

  private syncFiltros(): void {
    for (const btn of document.querySelectorAll<HTMLButtonElement>('#rank-modes .rank-tab')) {
      const tabMode = isRankingMode(btn.dataset.mode) ? btn.dataset.mode : 'campanha';
      btn.classList.toggle('sel', tabMode === this.mode);
    }
    for (const btn of document.querySelectorAll<HTMLButtonElement>('#rank-chars .rank-pill')) {
      btn.classList.toggle('sel', (btn.dataset.char || null) === this.charId);
    }
  }

  private async load(): Promise<void> {
    const box = $('rank-list');
    box.innerHTML = '<p class="zona-vazia">Consultando o cartório eleitoral...</p>';
    const meuSeq = ++this.seq;

    let data: RankingResponse;
    try {
      const params = new URLSearchParams({ mode: this.mode });
      if (this.charId) params.set('char', this.charId);
      const res = await fetch(`/api/ranking?${params}`);
      data = (await res.json()) as RankingResponse;
      if (!data.ok) throw new Error('resposta não-ok');
    } catch {
      if (meuSeq === this.seq) {
        box.innerHTML = '<p class="zona-vazia">O cartório caiu no meio da apuração. Tente de novo em instantes.</p>';
      }
      return;
    }
    if (meuSeq !== this.seq) return;   // o eleitor já trocou de filtro

    if (!data.configured) {
      box.innerHTML = '<p class="zona-vazia">Ranking indisponível neste servidor (sem banco de dados — modo dev).</p>';
      return;
    }
    if (!data.rows.length) {
      const vazio: Record<RankingMode, string> = {
        campanha: 'Nenhuma pontuação registrada ainda. Seja o primeiro nome da história eleitoral.',
        infinito: 'Ninguém encarou o MODO INFINITO ainda. Sobreviva aos 20 minutos e aceite o caos.',
        diaria: 'Ninguém votou na ELEIÇÃO DO DIA ainda — o placar zera à meia-noite de Brasília. Seja o primeiro da seção.',
        semanal: 'Boca de urna vazia: ninguém pontuou na ELEIÇÃO DA SEMANA ainda. As urnas fecham domingo às 17h.',
      };
      box.innerHTML = `<p class="zona-vazia">${vazio[this.mode]}</p>`;
      return;
    }

    box.innerHTML = '';
    data.rows.forEach((r, i) => box.appendChild(this.buildRow(r, i + 1)));
  }

  private buildRow(r: RankingRow, pos: number): HTMLElement {
    const row = document.createElement('div');
    // pódio (1º-3º) ganha cor de medalha via classes rank-ouro/prata/bronze
    row.className = `rank-row${pos === 1 ? ' rank-ouro' : pos === 2 ? ' rank-prata' : pos === 3 ? ' rank-bronze' : ''}`;
    const char = CHARACTER_TABLE[r.charId];
    const candidato = char ? splitNome(char.nome).nome : r.charId;
    row.innerHTML = `
      <span class="rank-pos">${pos}</span>
      <span class="rank-nome">${charImg(r.charId, 26)}<b>${esc(r.name)}</b></span>
      <span class="rank-char rank-col-char">${esc(candidato)}</span>
      <span class="rank-pontos num">${r.score.toLocaleString('pt-BR')}</span>
      <span class="rank-tempo num">${fmtTempo(r.timeSurvived)}</span>
      <span class="rank-kills num rank-col-kills">${r.kills.toLocaleString('pt-BR')}</span>`;
    return row;
  }
}
